import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL, BFF_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'

interface ApproveSandboxParams {
  sandboxId: number
  assignPhone?: boolean
}

interface RejectSandboxParams {
  sandboxId: number
  reason: string
}

interface RevokeSandboxParams {
  sandboxId: number
}

interface ReactivateSandboxParams {
  sandboxId: number
}

interface UpdateSandboxExpiryParams {
  sandboxId: number
  expiresAt: string
}

interface CreateDirectSandboxParams {
  name: string
  email: string
  companyName: string
  phoneNumber?: string
}

interface ForceActivateSandboxParams {
  sandboxId: number
}

interface ReactivateSandboxWithPhoneParams {
  sandboxId: number
  expiresAt: string
}

// zero-service's CORS allowedHeaders does not include X-GA (or the dev-only
// ngrok header) that apiHeaders sends, so a full apiHeaders() call fails the
// preflight. Send just the auth token, which zero-service's guard requires.
const bffHeaders = (): HeadersInit => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-LINQ-API-TOKEN': store.getState().user.apiToken || '',
})

const approveSandbox = async ({ sandboxId }: ApproveSandboxParams) => {
  const response = await fetch(`${BFF_URL}/sandbox/admin/activate`, {
    method: API_METHODS.POST,
    headers: bffHeaders(),
    body: JSON.stringify({ sandboxId }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message || errorData.error || 'Failed to approve sandbox'
    throw new Error(errorMessage)
  }

  return response.json()
}

const rejectSandbox = async ({ sandboxId, reason }: RejectSandboxParams) => {
  // Reject stays on Rolodeq — no PNS involvement
  const response = await fetch(
    `${BASE_API_URL}/admin/api/v4/sandboxes/${sandboxId}/reject`,
    {
      method: API_METHODS.POST,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify({ reason }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message || errorData.error || 'Failed to reject sandbox'
    throw new Error(errorMessage)
  }

  return response.json()
}

const revokeSandbox = async ({ sandboxId }: RevokeSandboxParams) => {
  const response = await fetch(`${BFF_URL}/sandbox/admin/revoke`, {
    method: API_METHODS.POST,
    headers: bffHeaders(),
    body: JSON.stringify({ sandboxId }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message || errorData.error || 'Failed to revoke sandbox'
    throw new Error(errorMessage)
  }

  return response.json()
}

const updateSandboxExpiry = async ({
  sandboxId,
  expiresAt,
}: UpdateSandboxExpiryParams) => {
  const response = await fetch(`${BFF_URL}/sandbox/admin/update`, {
    method: API_METHODS.PATCH,
    headers: bffHeaders(),
    body: JSON.stringify({ sandboxId, expiresAt }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message ||
      errorData.error ||
      'Failed to update sandbox expiry'
    throw new Error(errorMessage)
  }

  return response.json()
}

const reactivateSandbox = async ({ sandboxId }: ReactivateSandboxParams) => {
  const { currentUser } = store.getState().user
  const response = await fetch(`${BFF_URL}/sandbox/admin/reactivate`, {
    method: API_METHODS.POST,
    headers: bffHeaders(),
    body: JSON.stringify({
      sandboxId,
      activatedByUserId: currentUser?.id,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message ||
      errorData.error ||
      'Failed to reactivate sandbox'
    throw new Error(errorMessage)
  }

  return response.json()
}

const createDirectSandbox = async ({
  name,
  email,
  companyName,
  phoneNumber,
}: CreateDirectSandboxParams) => {
  const response = await fetch(
    `${BASE_API_URL}/admin/api/v4/sandboxes/create_direct`,
    {
      method: API_METHODS.POST,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify({
        name,
        email,
        company_name: companyName,
        phone_number: phoneNumber,
      }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message || errorData.error || 'Failed to create sandbox'
    throw new Error(errorMessage)
  }

  return response.json()
}

export function useApproveSandbox() {
  const queryClient = useQueryClient()

  return useMutation(approveSandbox, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}

export function useRejectSandbox() {
  const queryClient = useQueryClient()

  return useMutation(rejectSandbox, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}

export function useRevokeSandbox() {
  const queryClient = useQueryClient()

  return useMutation(revokeSandbox, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}

export function useUpdateSandboxExpiry() {
  const queryClient = useQueryClient()

  return useMutation(updateSandboxExpiry, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}

export function useReactivateSandbox() {
  const queryClient = useQueryClient()

  return useMutation(reactivateSandbox, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}

export function useCreateDirectSandbox() {
  const queryClient = useQueryClient()

  return useMutation(createDirectSandbox, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}

const forceActivateSandbox = async ({
  sandboxId,
}: ForceActivateSandboxParams) => {
  // Through the BFF: Rolodeq's /api/internal routes require a shared service
  // secret that must not live in a browser. zero-service holds it.
  const response = await fetch(`${BFF_URL}/sandbox/admin/force-activate`, {
    method: API_METHODS.POST,
    headers: bffHeaders(),
    body: JSON.stringify({ sandboxId }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message ||
      errorData.error ||
      'Failed to force activate sandbox'
    throw new Error(errorMessage)
  }

  return response.json()
}

export function useForceActivateSandbox() {
  const queryClient = useQueryClient()

  return useMutation(forceActivateSandbox, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}

const reactivateSandboxWithPhone = async ({
  sandboxId,
  expiresAt,
}: ReactivateSandboxWithPhoneParams) => {
  const response = await fetch(
    `${BFF_URL}/sandbox/admin/reactivate-with-phone`,
    {
      method: API_METHODS.POST,
      headers: bffHeaders(),
      body: JSON.stringify({ sandboxId, expiresAt }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage =
      errorData.error?.message ||
      errorData.error ||
      'Failed to reactivate sandbox'
    throw new Error(errorMessage)
  }

  return response.json()
}

export function useReactivateSandboxWithPhone() {
  const queryClient = useQueryClient()

  return useMutation(reactivateSandboxWithPhone, {
    onSuccess: () => {
      queryClient.invalidateQueries(['sandboxes'])
      queryClient.invalidateQueries(['sandbox-stats'])
    },
  })
}
